import { Chip, IconButton, Skeleton, Stack, TableCell, TableRow, Tooltip, Typography, TypographyProps } from '@ethings/ui-toolkit';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useOdometerReadingQuery } from '../../hooks';
import { GPS_ODOMETER_FEATURE } from '../../types/odometer';
import { formatDistanceString } from '../../util';
import { useBoolFlag } from '../flags';
import { FormatTimestamp } from '../format';
import { Icon } from '../icons';
import { ListPaperColumn } from '../list-item';
import { MicroPortalConfig, MicroPortalLauncher } from '../micro-portal';

const ODOMETER_FEATURE_FLAG = 'portals-odometer-gps-fallback-feature';
const ODOMETER_OFFSET_EDIT_FLAG = 'portals-gps-odometer-offset-feature';
const CAN_SOURCE_LABEL_FLAG = 'portals-odometer-can-source-label-feature';
const GPS_FEATURE = 'distance/gps';
const CAN_FEATURE = 'distance/primary';

const SOURCE_HINTS = {
  [GPS_FEATURE]: { icon: 'Signal', label: 'GPS', tooltipKey: 'common:component.odometer-display.hint.gps-source' },
  [CAN_FEATURE]: { icon: 'Unplug', label: 'CAN', tooltipKey: 'common:component.odometer-display.hint.can-source' },
} as const;

const MICRO_PORTAL_CONFIG: MicroPortalConfig = {
  app: 'accumulator-calibration',
  type: 'dialog',
  data: { feature: GPS_ODOMETER_FEATURE, name: 'Distance' },
  action: { icon: 'Cog' },
  element: null,
  isDismissible: true,
};

type DisplayMode = 'default' | 'table-row';

interface Props {
  thingId: string;
  label?: ReactNode;
  noReadingLabel?: ReactNode;
  variant?: TypographyProps['variant'];
  wrap?: boolean;
  displayMode?: DisplayMode;
  isEditable?: boolean;
  'data-testid'?: string;
}

export const OdometerDisplay: FC<Props> = ({
  thingId,
  label,
  noReadingLabel,
  variant = 'inherit',
  wrap,
  displayMode = 'default',
  isEditable,
  ...props
}) => {
  const { t } = useTranslation(['common']);
  const flagEnabled = useBoolFlag(ODOMETER_FEATURE_FLAG);
  const offsetEditEnabled = useBoolFlag(ODOMETER_OFFSET_EDIT_FLAG);
  const canSourceLabelEnabled = useBoolFlag(CAN_SOURCE_LABEL_FLAG);
  const { data: reading, isLoading } = useOdometerReadingQuery(thingId, { enabled: Boolean(flagEnabled) });
  const noReadingContent = noReadingLabel ?? t('common:component.recent-events-card.hint.no-data', { value: 30, measurement: 'days' });

  if (!flagEnabled) return null;

  const isGpsSourced = reading?.feature === GPS_FEATURE;
  const canLaunchMicroPortal = Boolean(isEditable && offsetEditEnabled && thingId && isGpsSourced);
  // appliedOffsetMeters is 0 both when there's no offset record and when a confirmed offset nets to zero drift.
  const needsCalibration = isGpsSourced && reading?.appliedOffsetMeters === 0;

  const sourceHintConfig = isGpsSourced ? SOURCE_HINTS[GPS_FEATURE] : reading?.feature === CAN_FEATURE && canSourceLabelEnabled ? SOURCE_HINTS[CAN_FEATURE] : null;

  const sourceHint = sourceHintConfig ? (
    <Tooltip title={t(sourceHintConfig.tooltipKey)}>
      <Typography component="span" variant="caption" color="text.secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <Icon icon={sourceHintConfig.icon} style={{ fontSize: '0.75rem' }} />{sourceHintConfig.label}
      </Typography>
    </Tooltip>
  ) : null;

  const labelContent = label && sourceHint ? (
    <Stack direction="column" spacing={0}>
      <Typography variant="body2">{label}</Typography>
      {sourceHint}
    </Stack>
  ) : label;

  const cogIcon = canLaunchMicroPortal ? (
    <MicroPortalLauncher
      app={MICRO_PORTAL_CONFIG.app}
      type={MICRO_PORTAL_CONFIG.type}
      data={{ ...MICRO_PORTAL_CONFIG.data, thingId }}
      element={
        <Tooltip title={t('common:component.odometer-display.action.modify')}>
          <IconButton
            size="small"
            data-testid="odometer-calibration-launcher"
            sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
            aria-label={t('common:component.odometer-display.action.modify')}
          >
            <Icon icon={MICRO_PORTAL_CONFIG.action.icon || 'Cog'} style={{ fontSize: '1rem' }} />
          </IconButton>
        </Tooltip>
      }
      isDismissible={true}
      scrolling={'yes'}
    />
  ) : null;

  const needsCalibrationChip = needsCalibration ? (
    <Chip
      color="error"
      variant="light"
      size="small"
      data-testid="odometer-needs-calibration-chip"
      label={t('common:component.odometer-display.hint.needs-calibration')}
    />
  ) : null;

  const valueText = isLoading ? (
    <Skeleton />
  ) : reading ? (
    <Typography variant={variant}>{formatDistanceString(reading.calibratedValueMeters, undefined, 'short')}</Typography>
  ) : (
    <Typography variant={variant} component="div" fontStyle="italic" color="text.secondary">{noReadingContent}</Typography>
  );

  const valueContent = (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {valueText}
      {!isLoading && needsCalibrationChip}
      {!isLoading && cogIcon}
    </Stack>
  );

  if (displayMode === 'table-row') {
    if (!isLoading && !reading) return null;

    return (
      <TableRow data-testid={props['data-testid']}>
        <TableCell>{labelContent}</TableCell>
        <TableCell>{valueContent}</TableCell>
        <TableCell align="right">
          {reading && !isLoading && <FormatTimestamp format="relative" value={reading.occurred} />}
        </TableCell>
      </TableRow>
    );
  }

  const content = reading && !isLoading ? (
    <Stack alignItems={label ? 'flex-start' : 'flex-end'} gap={0}>
      {valueContent}
      <Typography variant="caption" color="text.secondary">
        <FormatTimestamp format="relative" value={reading.occurred} />
      </Typography>
    </Stack>
  ) : valueContent;

  if (!label) return content;

  return (
    <ListPaperColumn label={labelContent} wrap={wrap} data-testid={props['data-testid']}>
      {content}
    </ListPaperColumn>
  );
};
